import { usePageImage } from "@/lib/usePageImage";

import styles from "./ReadingJobScreen.module.css";

/**
 * Where the sheet comes from. Two pages that show the same drawing: the one
 * the reading turned (`extraction/pages/1`), which exists once the worker
 * has taken the job, and the stored file's own first page, which exists from
 * the upload on. The first wins once it has loaded; until then, and if it
 * never does, the second stands in.
 */
export interface SheetSource {
  reading: string | null;
  document: string | null;
}

/**
 * The drawing on „Zeichnung lesen“ (Kauf-Test 11.10.2026): the screen was
 * three step cards over an empty field, and nothing said *what* was being
 * read. Now the person's own sheet stands there — on the left, where the
 * review puts the Original a minute later, so it does not jump when the
 * reading is done.
 *
 * Still, on purpose. Nothing moves on it and nothing lies over it: it
 * carries the customer's handwritten figures, and the mesh never goes behind
 * numbers (CI §3). That the sheet is being read is said in its caption and
 * by the running step beside it.
 */
export function ReadingSheet({
  source,
  note,
  state = null,
  missing,
  alt = "Seite 1 deiner Zeichnung",
}: {
  source: SheetSource;
  /** What the sheet is — a file name, or „Seite 2 aus „Angebot.pdf““. Cut short where it has to be. */
  note: string;
  /** What is happening to it („wird gelesen“) — never cut, whatever the name before it. */
  state?: string | null;
  missing: string;
  alt?: string;
}) {
  const reading = usePageImage(source.reading ?? "");
  const stored = usePageImage(source.document ?? "");
  // Whatever is in hand is shown while the other loads: a page that arrives
  // replaces the one that was there instead of a blank in between.
  const shown = reading.status === "loaded" ? reading : stored.status === "loaded" ? stored : null;
  const asked = source.reading !== null || source.document !== null;
  const failed = asked && reading.status !== "loading" && stored.status !== "loading" && shown === null;

  return (
    <figure className={styles.sheet} data-role="reading-sheet">
      <figcaption className={styles.sheetHead}>
        <span className="label">Deine Zeichnung</span>
        {note !== "" ? (
          <span className={styles.sheetNote} title={note}>
            {note}
          </span>
        ) : null}
        {state !== null ? (
          <span className={styles.sheetState}>
            {note !== "" ? " · " : null}
            {state}
          </span>
        ) : null}
      </figcaption>
      {/* The page is a sheet (design 2026-10-24 §5.3): paper in the dark,
          its caption in the mode — the page itself, not a frame round it
          (UX-Gutachten 2, B-10: a white passe-partout round the warm page was
          the brightest thing on the dark screen). Round it the desk's well,
          and the page on the drawings' one paper tone (`--blatt-grund`). */}
      <div className={styles.sheetBody}>
        {shown !== null ? (
          <img className={styles.sheetImage} src={shown.src} alt={alt} data-blatt="zeichnung" />
        ) : failed ? (
          <p className={styles.sheetMissing}>{missing}</p>
        ) : (
          <div className={styles.sheetWait} aria-hidden="true" />
        )}
      </div>
    </figure>
  );
}
